// CP-17 — the pre-test connection check. Pure, React-free, fully injectable.
//
// Measures three things against OUR OWN ORIGIN — the candidate flow is
// zero-cross-origin by policy (CSP `connect-src 'self'`), so there is no
// speed-test service here, only endpoints and files we already serve:
//   1. round-trip time      sequential tiny GETs of `probeUrl` (/api/health —
//                           a no-DB handler with no rate limit)
//   2. stability            how many of those probes were lost or timed out
//   3. download throughput  ONE fetch of `downloadUrl` (a vendored Pyodide
//                           file every code test downloads anyway)
// and folds them into an ADVISORY verdict. Nothing here blocks the candidate,
// nothing is sent to the server about the result, nothing is stored.
//
// Raw `fetch` on purpose — `apiFetch` retries transient failures, which is the
// right thing for reads and exactly the wrong thing for a probe.

export type ConnectionVerdict = "good" | "fair" | "poor" | "offline";
export type ConnectionPhase = "latency" | "download";
/** What lowered the verdict — drives the copy, so "far from the server" and
 *  "packets are being lost" are not conflated into one scary sentence. */
export type ConnectionConcern = "drops" | "latency" | "bandwidth";

export interface ConnectionCheckResult {
  verdict: ConnectionVerdict;
  /** Empty when `good`. */
  concerns: ConnectionConcern[];
  /** Scored probes only — the warm-up probe is excluded. */
  probes: { ok: number; total: number };
  /** Null when no scored probe succeeded. `jitterMs` = max − median, display
   *  only: on five samples it flags perfectly healthy links, so it never
   *  classifies. */
  latency: { medianMs: number; jitterMs: number } | null;
  /** Null when throughput could not be measured — that never lowers the
   *  verdict. `complete: false` = the cap hit and the figure is from the bytes
   *  received so far. */
  download: { mbps: number; bytes: number; ms: number; complete: boolean } | null;
}

export interface ConnectionCheckOptions {
  /** Tiny same-origin JSON endpoint for round trips. */
  probeUrl: string;
  /** Larger same-origin file for throughput. */
  downloadUrl: string;
  /** The test has PYTHON coding questions (a ~14 MB Pyodide download) → the
   *  stricter bandwidth bars apply. An MCQ-only test moves small JSON and must
   *  not be told a 3 Mbps line is "fair" — nor must a JavaScript / SQL-only
   *  test (FEAT-6), whose runtimes are ~1 MB together. */
  needsBandwidth: boolean;
  /** Scored probes (a warm-up probe always runs first). */
  probes?: number;
  probeGapMs?: number;
  /** Per probe. There is deliberately NO wall-clock phase budget: a
   *  backgrounded tab throttles timers but not fetches, so per-fetch timing
   *  stays honest while a budget would count unrun probes as drops. */
  probeTimeoutMs?: number;
  /** Whole download phase, from the fetch call. On hit: partial figure. */
  downloadCapMs?: number;
  /** Caller cancellation (unmount, re-run) → rejects with an AbortError. */
  signal?: AbortSignal;
  onPhase?: (phase: ConnectionPhase) => void;
  // Test seams.
  fetchImpl?: (url: string, init: RequestInit) => Promise<Response>;
  /** Monotonic clock. Default `performance.now` — never `Date.now`, which an
   *  NTP step can move mid-measurement. */
  now?: () => number;
  isOnline?: () => boolean;
}

// ── Thresholds — ADVISORY and tunable ─────────────────────────────────────
// The origin is Fly Frankfurt. A perfect link from India or the US west coast
// sits at 150–300 ms RTT, and mid-test traffic is small JSON POSTs on a serial
// queue, so a 500 ms round trip is harmless. The latency bars flag a genuinely
// laggy link, not geography.
export const LATENCY_FAIR_ABOVE_MS = 350;
export const LATENCY_POOR_ABOVE_MS = 1000;
/** Scored probes lost. */
export const DROPS_FAIR_AT = 1;
export const DROPS_POOR_AT = 2;
/** Python code tests preload ~14 MB of Pyodide while the candidate fills the
 *  form: 5 Mbps ≈ 22 s (just fits), 1.5 Mbps ≈ 75 s (a visible "Loading
 *  Python sandbox…" stall on the first coding question). JavaScript / SQL
 *  runtimes are ~1 MB and use the MCQ bars. */
export const BANDWIDTH_BARS_CODE = { fairBelowMbps: 5, poorBelowMbps: 1.5 };
/** Everything else the flow downloads is JSON plus a 190 KB face model. */
export const BANDWIDTH_BARS_MCQ = { fairBelowMbps: 1.5, poorBelowMbps: 0.5 };

const DEFAULT_PROBES = 5;
const DEFAULT_PROBE_GAP_MS = 150;
const DEFAULT_PROBE_TIMEOUT_MS = 2000;
const DEFAULT_DOWNLOAD_CAP_MS = 5000;
/** A COMPLETE body smaller than this cannot be the probe file — the SPA
 *  fallback (index.html + 200) or a truncated asset. Not applied to a capped,
 *  partial download: 60 KB in five seconds is a real, terrible link. */
const MIN_USEFUL_DOWNLOAD_BYTES = 64 * 1024;
/** A capped download whose body phase was shorter than this had its window
 *  eaten by time-to-first-byte; the figure would be noise. */
const MIN_PARTIAL_DOWNLOAD_MS = 500;

export function median(xs: number[]): number {
  if (xs.length === 0) return NaN;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export interface ConnectionStats {
  probesOk: number;
  probesTotal: number;
  medianMs: number | null;
  downloadMbps: number | null;
}

/** Pure fold of the measurements into a verdict. The worst concern wins. */
export function classify(
  stats: ConnectionStats,
  needsBandwidth: boolean,
): { verdict: ConnectionVerdict; concerns: ConnectionConcern[] } {
  if (stats.probesOk === 0) return { verdict: "offline", concerns: [] };
  const concerns: ConnectionConcern[] = [];
  let worst = 0; // 0 good · 1 fair · 2 poor
  const note = (concern: ConnectionConcern, level: 1 | 2): void => {
    concerns.push(concern);
    worst = Math.max(worst, level);
  };

  const dropped = stats.probesTotal - stats.probesOk;
  if (dropped >= DROPS_POOR_AT) note("drops", 2);
  else if (dropped >= DROPS_FAIR_AT) note("drops", 1);

  if (stats.medianMs !== null) {
    if (stats.medianMs > LATENCY_POOR_ABOVE_MS) note("latency", 2);
    else if (stats.medianMs > LATENCY_FAIR_ABOVE_MS) note("latency", 1);
  }

  if (stats.downloadMbps !== null) {
    const bars = needsBandwidth ? BANDWIDTH_BARS_CODE : BANDWIDTH_BARS_MCQ;
    if (stats.downloadMbps < bars.poorBelowMbps) note("bandwidth", 2);
    else if (stats.downloadMbps < bars.fairBelowMbps) note("bandwidth", 1);
  }

  return {
    verdict: worst === 2 ? "poor" : worst === 1 ? "fair" : "good",
    concerns,
  };
}

export async function runConnectionCheck(
  opts: ConnectionCheckOptions,
): Promise<ConnectionCheckResult> {
  const fetchImpl =
    opts.fetchImpl ?? ((url: string, init: RequestInit) => fetch(url, init));
  const now = opts.now ?? (() => performance.now());
  const isOnline =
    opts.isOnline ??
    (() => typeof navigator === "undefined" || navigator.onLine !== false);
  const probeCount = opts.probes ?? DEFAULT_PROBES;
  const gapMs = opts.probeGapMs ?? DEFAULT_PROBE_GAP_MS;
  const probeTimeoutMs = opts.probeTimeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS;
  const downloadCapMs = opts.downloadCapMs ?? DEFAULT_DOWNLOAD_CAP_MS;
  const signal = opts.signal;

  throwIfAborted(signal);
  if (!isOnline()) {
    // The browser already knows. Don't spend ten seconds proving it.
    return {
      verdict: "offline",
      concerns: [],
      probes: { ok: 0, total: 0 },
      latency: null,
      download: null,
    };
  }

  opts.onPhase?.("latency");
  // Warm-up, discarded: the h2 connection to Fly's edge may have idled out
  // since /preview loaded, so the first sample would be TLS resumption, not a
  // round trip. It is not scored either way — five clean samples decide.
  await probeOnce(opts.probeUrl, fetchImpl, now, probeTimeoutMs, signal);

  const samples: number[] = [];
  let attempted = 0;
  for (let i = 0; i < probeCount; i++) {
    if (gapMs > 0) await sleep(gapMs, signal);
    throwIfAborted(signal);
    attempted++;
    const ms = await probeOnce(
      opts.probeUrl,
      fetchImpl,
      now,
      probeTimeoutMs,
      signal,
    );
    if (ms !== null) samples.push(ms);
  }

  const probes = { ok: samples.length, total: attempted };
  const latency =
    samples.length > 0
      ? {
          medianMs: median(samples),
          jitterMs: Math.max(...samples) - median(samples),
        }
      : null;

  // Nothing answered → there is no bandwidth to measure.
  let download: ConnectionCheckResult["download"] = null;
  if (probes.ok > 0) {
    throwIfAborted(signal);
    opts.onPhase?.("download");
    download = await measureDownload(
      opts.downloadUrl,
      fetchImpl,
      now,
      downloadCapMs,
      signal,
    );
  }

  const { verdict, concerns } = classify(
    {
      probesOk: probes.ok,
      probesTotal: probes.total,
      medianMs: latency?.medianMs ?? null,
      downloadMbps: download?.mbps ?? null,
    },
    opts.needsBandwidth,
  );
  return { verdict, concerns, probes, latency, download };
}

// ── Internals ─────────────────────────────────────────────────────────────

/** One timed round trip. Null = lost (network error, timeout, non-2xx, or a
 *  non-JSON body — the SPA fallback answers a wrong URL with index.html and a
 *  200, which must not read as a healthy probe). A caller abort propagates. */
async function probeOnce(
  url: string,
  fetchImpl: (url: string, init: RequestInit) => Promise<Response>,
  now: () => number,
  timeoutMs: number,
  outer: AbortSignal | undefined,
): Promise<number | null> {
  const ctrl = new AbortController();
  const onOuterAbort = (): void => ctrl.abort();
  outer?.addEventListener("abort", onOuterAbort, { once: true });
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const t0 = now();
  try {
    // `no-store` bypasses the HTTP cache for the read AND the write, so no
    // query-string cache-buster is needed (and there is no CDN to defeat).
    const res = await fetchImpl(url, { cache: "no-store", signal: ctrl.signal });
    if (!res.ok || !contentType(res).startsWith("application/json")) return null;
    // Drain the (tiny) body so the sample is the whole round trip.
    await res.arrayBuffer();
    return now() - t0;
  } catch {
    if (outer?.aborted) throw abortError();
    return null;
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", onOuterAbort);
  }
}

/** One timed download. Null = could not measure (never lowers the verdict). */
async function measureDownload(
  url: string,
  fetchImpl: (url: string, init: RequestInit) => Promise<Response>,
  now: () => number,
  capMs: number,
  outer: AbortSignal | undefined,
): Promise<ConnectionCheckResult["download"]> {
  const ctrl = new AbortController();
  // Set BEFORE abort() so the read loop can tell a cap from a caller cancel.
  let capped = false;
  const onOuterAbort = (): void => ctrl.abort();
  outer?.addEventListener("abort", onOuterAbort, { once: true });
  const timer = setTimeout(() => {
    capped = true;
    ctrl.abort();
  }, capMs);
  try {
    // `reload` skips the cache for the READ but stores the response when it
    // completes — so on a code test a completed probe warms the immutable
    // cache for Pyodide's own default-mode fetch of this very URL, instead of
    // the bytes being wasted. A capped (aborted) download is NOT stored. No
    // query string, for the same reason: the cache key must be the real URL.
    const res = await fetchImpl(url, { cache: "reload", signal: ctrl.signal });
    if (!res.ok || contentType(res).startsWith("text/html")) return null;

    // Timing starts at headers-arrived — the connection setup and
    // time-to-first-byte are the latency probe's business. The window still
    // includes TCP slow-start, so the figure is conservative, never inflated.
    const t0 = now();
    let bytes = 0;
    let complete = false;
    if (!res.body) {
      // No streaming (older Safari) — all or nothing; a cap here yields
      // nothing partial, and the catch below returns null.
      bytes = (await res.arrayBuffer()).byteLength;
      complete = true;
    } else {
      const reader = res.body.getReader();
      try {
        for (;;) {
          const { done, value } = await reader.read();
          if (done) {
            complete = true;
            break;
          }
          bytes += value.byteLength;
        }
      } catch (err) {
        // The cap fired mid-body: measure what arrived. Anything else
        // (caller cancel, a dropped connection) is handled by the outer catch.
        if (!capped) throw err;
      }
    }
    const ms = now() - t0;
    if (bytes === 0 || ms <= 0) return null;
    if (complete && bytes < MIN_USEFUL_DOWNLOAD_BYTES) return null;
    if (!complete && ms < MIN_PARTIAL_DOWNLOAD_MS) return null;
    return { mbps: (bytes * 8) / ms / 1000, bytes, ms, complete };
  } catch {
    if (outer?.aborted) throw abortError();
    return null;
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", onOuterAbort);
  }
}

function contentType(res: Response): string {
  try {
    return (res.headers.get("content-type") ?? "").toLowerCase();
  } catch {
    return "";
  }
}

function abortError(): DOMException {
  return new DOMException("The connection check was cancelled.", "AbortError");
}

function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted) throw abortError();
}

function sleep(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(abortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
